// src/components/OptionFields/OptionFieldGroup.tsx
// 拡張フィールドのグループ表示コンポーネント

import React, { useEffect } from 'react';
import { Typography } from 'antd';
import { OptionField } from '../../services/option-field-api';
import OptionFieldRenderer from './OptionFieldRenderer';

const { Title } = Typography;

interface OptionFieldGroupProps {
  title: string;
  fields: OptionField[];
  values: Record<string, any>;
  rangeFromValues?: Record<string, any>;
  rangeToValues?: Record<string, any>;
  onFieldChange: (fieldKey: string, value: any) => void;
  onRangeFromChange?: (fieldKey: string, value: any) => void;
  onRangeToChange?: (fieldKey: string, value: any) => void;
  prefix?: string;
  readonly?: boolean;
  errors?: Record<string, string>;
  kind?: 'detail' | 'search' | 'compare' | 'marge';
  bordered?: boolean;
  /**
   * 内部の選択肢モーダル（Type3 リストボックス）の zIndex
   * 親モーダルの zIndex より大きい値を指定すること
   */
  modalZIndex?: number;
}

/**
 * 拡張フィールドのグループを表示するコンポーネント
 */
export const OptionFieldGroup: React.FC<OptionFieldGroupProps> = ({
  title,
  fields,
  values,
  rangeFromValues = {},
  rangeToValues = {},
  onFieldChange,
  onRangeFromChange,
  onRangeToChange,
  prefix = '',
  readonly = false,
  kind = 'detail',
  errors = {},
  bordered = true,
  modalZIndex,
}) => {
  // 無効化されていないフィールドのみを表示
  const activeFields = fields?.filter(field => !field.invalidflag) || [];

  // 標準オプションを優先してソート
  const sortedFields = activeFields.sort((a, b) => {
    const standardOrder: Record<string, number> = {
      'companyfurigana': 1,
      'furigana': 2,
      'office': 3,
      'postng': 4
    };
  
    const aOrder = standardOrder[a.optionfieldkey];
    const bOrder = standardOrder[b.optionfieldkey];
    
    if (aOrder && bOrder) return aOrder - bOrder;
    if (aOrder) return -1;
    if (bOrder) return 1;
    
    return (a.disporder || 0) - (b.disporder || 0);
  });

  // デフォルト値を即座に適用する関数
  const applyDefaultValues = () => {
    // 検索画面では使用しない
    if (kind === 'search' || !fields || fields.length === 0) return;
    
    let hasChanges = false;
    sortedFields.forEach((field) => {
      const fieldKey = field.optionfieldkey;
      const currentValue = values[fieldKey];
      const defaultValue = field.defaultValue || field.optionconfiginfoMap?.default_value;
      
      // 値が未設定でデフォルト値がある場合のみ設定
      if ((currentValue === undefined || currentValue === null) && defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
        hasChanges = true;
        // チェックボックスの場合は boolean/string 変換
        if (field.optionfieldtype === 2) {
          const booleanValue = defaultValue === 't' || defaultValue === 'true' || defaultValue === true;
          const finalValue = booleanValue ? 't' : 'f';
          onFieldChange(fieldKey, finalValue);
        } else {
          onFieldChange(fieldKey, defaultValue);
        }
      }
    });
    
    return hasChanges;
  };

  // フィールドが変更された時にデフォルト値を適用
  useEffect(() => {
    applyDefaultValues();
  }, [fields]); // fieldsの変化のみ監視

  if (!fields || fields.length === 0) {
    return null;
  }

  const containerStyle = bordered
    ? {
        marginBottom: '24px',
        backgroundColor: '#fafafa',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        padding: '16px'
      }
    : { marginBottom: '24px' };

  return (
    <div style={containerStyle}>
      {title && (
        <Title level={4} style={{ margin: '0 0 16px 0' }}>{title}</Title>
      )}
      {sortedFields.map((field) => {
        const fieldKey = field.optionfieldkey;
        const currentValue = values[fieldKey];
        
        return (
          <OptionFieldRenderer
            key={field.optionfieldno}
            field={field}
            value={currentValue}
            rangeFromValue={rangeFromValues[fieldKey]}
            rangeToValue={rangeToValues[fieldKey]}
            onChange={(value) => onFieldChange(fieldKey, value)}
            onRangeFromChange={(value) => onRangeFromChange?.(fieldKey, value)}
            onRangeToChange={(value) => onRangeToChange?.(fieldKey, value)}
            prefix={prefix}
            readonly={readonly}
            validationError={errors[fieldKey]}
            kind={kind}
            modalZIndex={modalZIndex}
          />
        );
      })}
    </div>
  );
};

export default OptionFieldGroup;